import React, { useEffect, useRef } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import styled from 'styled-components'
import { srConfig } from '@config'
import sr from '@utils/sr'

const StyledAboutSection = styled.section`
	max-width: 1000px;

	.inner {
		display: grid;
		grid-template-columns: 3fr 2fr;
		grid-gap: 50px;

		@media (max-width: 768px) {
			display: block;
		}
	}
`

const StyledText = styled.div`
	ul.skills-list {
		display: grid;
		grid-template-columns: repeat(2, minmax(140px, 200px));
		grid-gap: 0 10px;
		padding: 0;
		margin: 20px 0 0 0;
		overflow: hidden;
		list-style: none;

		li {
			position: relative;
			margin-bottom: 10px;
			padding-left: 20px;
			font-family: var(--font-mono);
			font-size: var(--fz-xs);

			&:before {
				content: '♦';
				position: absolute;
				left: 0;
				color: var(--blue);
				font-size: var(--fz-sm);
				line-height: 16px;
			}
		}
	}
`

const StyledPic = styled.div`
	position: relative;
	max-width: 300px;

	@media (max-width: 768px) {
		margin: 50px auto 0;
		width: 70%;
	}

	.wrapper {
		${({ theme }) => theme.mixins.boxShadow};
		display: block;
		position: relative;
		width: 100%;
		border-radius: var(--border-radius);
		background-color: var(--blue);

		&:hover,
		&:focus {
			outline: 0;

			&:after {
				top: -15px;
				left: 15px;
				// transform: translate(-8px, 8px);
			}

			.img {
				filter: none;
				mix-blend-mode: normal;
			}
		}

		.img {
			position: relative;
			border-radius: var(--border-radius);
			mix-blend-mode: multiply;
			filter: grayscale(100%) contrast(1);
			transition: var(--transition);
		}

		&:before,
		&:after {
			content: '';
			display: block;
			position: absolute;
			width: 100%;
			height: 100%;
			border-radius: var(--border-radius);
			transition: var(--transition);
		}

		&:before {
			top: 0;
			left: 0;
			background-color: var(--navy);
			mix-blend-mode: screen;
		}

		&:after {
			border: 2px solid var(--blue);
			box-shadow: 0 0 8px 4px var(--blue-tint), inset 0 0 8px 4px var(--blue-tint);
			top: -20px;
			left: 20px;
			z-index: -1;
		}
	}
`

const About = () => {
	const data = useStaticQuery(graphql`
		query {
			avatar: file(sourceInstanceName: { eq: "images" }, relativePath: { eq: "me.jpg" }) {
				childImageSharp {
					gatsbyImageData(width: 500, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
				}
			}
		}
	`)

	const revealContainer = useRef(null)

	useEffect(() => {
		sr.reveal(revealContainer.current, srConfig())
	}, [])

	const skills = [
		'JavaScript (ES6+)',
		'Node.js',
		'MongoDB',
		'React',
		'TypeScript',
		'Python',
		'GenAI',
		'LangChain',
		'LangGraph',
		'AWS',
		'Redis',
		'PostgreSQL',
		'GraphQL',
		'Bash',
	]

	return (
		<StyledAboutSection id="about" ref={revealContainer}>
			<h2 className="numbered-heading">About Me</h2>

			<div className="inner">
				<StyledText>
					<div>
						<p>
							Hello! I'm Rahul, a software engineer passionate about building digital products that
							thrive on the web. From crafting complex software architectures to engineering
							GenAI-powered applications, I strive for excellence in every line of code. I build
							products that look pixel-perfect while delivering a seamless, high-performance user
							experience.
						</p>

						<p>
							Currently, I'm working at a product-based company{' '}
							<a href="https://nielseniq.com">GfK - An NIQ Company</a> for 2 years as a Software
							Engineer. I have been involved in various stages of the full software development
							lifecycle (SDLC), from ideation and requirements gathering to system design, core
							development, testing, and deployment.
						</p>
						<p>
							I consistently leverage emerging technologies and modern tools to stay ahead of the
							curve, ensuring that every product I build adheres to the latest industry standards
							and best practices.
						</p>

						<p>Here are some technologies I've been working with:</p>
					</div>

					<ul className="skills-list">
						{skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
					</ul>
				</StyledText>

				<StyledPic>
					<div className="wrapper">
						<GatsbyImage image={getImage(data.avatar)} alt="Avatar" className="img" />
					</div>
				</StyledPic>
			</div>
		</StyledAboutSection>
	)
}

export default About
